import { axiosInstance } from '../utils/axios'
import { downloadFromApi } from '../utils/downloadFile'
import type {
  PortalReportDetail,
  PortalReportsPage,
  PortalReportSettings,
  ReportVersion,
} from '../types/flowBilling'

/**
 * Portal reports API — what a signed-in user can see of their own reports.
 *
 * Scoping happens server-side by group membership; there is nothing to pass
 * here. A report outside the caller's groups answers 404, which the UI shows
 * as "not found" rather than "forbidden", because the distinction would itself
 * disclose that another district's report exists.
 */

interface ApiResponse<T> {
  success: boolean
  data: T
}

const BASE_URL = '/reports'

export const reportsApi = {
  /**
   * Whether this user should see a Reports section at all.
   *
   * Answered by the server because visibility follows group membership, which
   * the client is never told. A bare boolean: it says whether you have reports,
   * not whose.
   */
  access: async (): Promise<boolean> => {
    const res = await axiosInstance.get<ApiResponse<{ hasAccess: boolean }>>(`${BASE_URL}/access`)
    return res.data.data.hasAccess
  },

  /**
   * The terms the caller's report is issued under, read-only.
   *
   * Recipients arrive as counts, not addresses — the server never sends them,
   * so there is nothing here to hide in the UI.
   */
  settings: async (): Promise<PortalReportSettings> => {
    const res = await axiosInstance.get<ApiResponse<PortalReportSettings>>(
      `${BASE_URL}/flow-billing/config`
    )
    return res.data.data
  },

  list: async (params: { page?: number; limit?: number; year?: number } = {}) => {
    const res = await axiosInstance.get<ApiResponse<PortalReportsPage>>(BASE_URL, { params })
    return res.data.data
  },

  get: async (id: number): Promise<PortalReportDetail> => {
    const res = await axiosInstance.get<ApiResponse<PortalReportDetail>>(`${BASE_URL}/${id}`)
    return res.data.data
  },

  /** Every version of the period this report covers, newest first. */
  versions: async (id: number): Promise<ReportVersion[]> => {
    const res = await axiosInstance.get<ApiResponse<{ versions: ReportVersion[] }>>(
      `${BASE_URL}/${id}/versions`
    )
    return res.data.data.versions
  },

  /**
   * Downloads take their name from the server's Content-Disposition. These
   * fallbacks are only reached if that header is unreadable — which it was,
   * until it was added to the API's CORS exposedHeaders. Built from the report
   * rather than its row id so even the fallback says what the file is.
   */
  downloadCsv: (id: number, fallback?: string) =>
    downloadFromApi(`${BASE_URL}/${id}/download.csv`, `${fallback || `report-${id}`}.csv`),

  downloadPdf: (id: number, fallback?: string) =>
    downloadFromApi(`${BASE_URL}/${id}/download.pdf`, `${fallback || `report-${id}`}.pdf`),
}
